import React, { useState } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM, Flex, Text, Button, Tag, Spinner } from "components/atoms";
import { SlideManager } from "components/organisms/CommonUI/SlidePopup/SlideManager";
import { SubscriptionPlanCode } from "common/models/types";
import { formatKrw } from "utils/subscriptionDisplay";
import { SUBSCRIPTION_TERMS_URL } from "common/constants";

/**
 * PhAI 멤버십 구독 등록 사이드 슬라이드.
 *
 * - PointsChargeSlide와 동일 구조 (sticky Header / scrollable Content / sticky Footer)
 * - ymyd-frontend PremiumUserSubscribePopup의 동의 체크 패턴
 * - 결제 진행 중에는 카드 선택 / 동의 토글 비활성화
 */

interface PlanOption {
  code: SubscriptionPlanCode;
  title: string;
  price: number;
  cycleLabel: string;
  highlight?: string;
}

const PLAN_OPTIONS: PlanOption[] = [
  {
    code: "PHAI_PREMIUM_MONTHLY",
    title: "월 정기결제",
    price: 30000,
    cycleLabel: "매월 자동결제 · 해지해도 잔여 기간 이용 가능",
  },
  {
    code: "PHAI_PREMIUM_YEARLY",
    title: "연 정기결제",
    price: 300000,
    cycleLabel: "매년 자동결제 · 해지해도 잔여 기간 이용 가능",
    highlight: "2개월 무료 · 약 16.7% 절약",
  },
];

interface Props {
  initialPlan?: SubscriptionPlanCode;
  isProcessing: boolean;
  onSubscribe: (plan: SubscriptionPlanCode) => void;
}

const SlideContainer = styled.div`
  width: 100%;
  height: 100%;
  background: ${COLOR_SYSTEM.get("Gray")[0]};
  display: flex;
  flex-direction: column;
`;

const HeaderSection = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")[100]};
  background: ${COLOR_SYSTEM.get("Gray")[0]};
  position: sticky;
  top: 0;
  z-index: 10;
`;

const ContentSection = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

const PlanCard = styled.div<{ selected?: boolean; disabled?: boolean }>`
  padding: 20px;
  margin-bottom: 16px;
  border: 1px solid
    ${({ selected }) =>
      selected ? COLOR_SYSTEM.get("Blue")[400] : COLOR_SYSTEM.get("Gray")[100]};
  border-radius: 12px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  background: ${({ selected }) =>
    selected ? COLOR_SYSTEM.get("Blue")[10] : COLOR_SYSTEM.get("Gray")[0]};
  transition: all 0.2s ease;
`;

const InfoBox = styled.div`
  padding: 16px;
  background: ${COLOR_SYSTEM.get("Gray")[50]};
  border-radius: 8px;
  margin-top: 20px;
  margin-bottom: 16px;
`;

const ConsentRow = styled.label<{ disabled?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  padding: 12px 0;
`;

const TermsLink = styled.span`
  color: ${COLOR_SYSTEM.get("Blue")[600]};
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;
`;

const FooterSection = styled.div`
  padding: 20px;
  background: ${COLOR_SYSTEM.get("Gray")[50]};
  border-top: 1px solid ${COLOR_SYSTEM.get("Gray")[100]};
`;

const PremiumSubscribeSlide: React.FC<Props> = ({
  initialPlan = "PHAI_PREMIUM_MONTHLY",
  isProcessing,
  onSubscribe,
}) => {
  const [plan, setPlan] = useState<SubscriptionPlanCode>(initialPlan);
  const [agreed, setAgreed] = useState(false);
  const selected = PLAN_OPTIONS.find((p) => p.code === plan) ?? PLAN_OPTIONS[0];

  const handleSelectPlan = (code: SubscriptionPlanCode) => {
    if (isProcessing) return;
    setPlan(code);
  };

  const handleConfirm = () => {
    if (!agreed || isProcessing) return;
    onSubscribe(plan);
  };

  // 약관 보기 — label 클릭(체크 토글)과 분리하기 위해 전파/기본동작 차단
  const handleOpenTerms = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(SUBSCRIPTION_TERMS_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <SlideContainer>
      <HeaderSection>
        <Flex alignItems="center" justifyContent="space-between">
          <Text textType="H2">PhAI 멤버십 구독</Text>
          <Button
            size="Small"
            type="Icon"
            variant="Ghost"
            color="Secondary"
            icon="X"
            onClick={() => SlideManager.hide()}
            disabled={isProcessing}
          />
        </Flex>
        <Text
          textType="Body2"
          color={COLOR_SYSTEM.get("Gray")[600]}
          style={{ display: "block", marginTop: 8 }}
        >
          요금제를 선택하고, 횟수 제한 없이 마음껏 환자를 챙겨보세요.
        </Text>
      </HeaderSection>

      <ContentSection>
        <div style={{ marginBottom: 16 }}>
          <Text textType="H4">요금제 선택</Text>
        </div>

        {PLAN_OPTIONS.map((opt) => (
          <PlanCard
            key={opt.code}
            selected={plan === opt.code}
            disabled={isProcessing}
            onClick={() => handleSelectPlan(opt.code)}
          >
            <Flex alignItems="center" justifyContent="space-between">
              <Text textType="H3">{opt.title}</Text>
              {opt.highlight && (
                <Tag size="Small" variant="Contained" color="Blue">
                  {opt.highlight}
                </Tag>
              )}
            </Flex>
            <Text
              textType="H2"
              color={COLOR_SYSTEM.get("Blue")[600]}
              style={{ display: "block", marginTop: 4 }}
            >
              {formatKrw(opt.price)}
            </Text>
            <Text
              textType="Body2"
              color={COLOR_SYSTEM.get("Gray")[600]}
              style={{ display: "block", marginTop: 4 }}
            >
              {opt.cycleLabel}
            </Text>
          </PlanCard>
        ))}

        <InfoBox>
          <Text
            textType="Body2Medium"
            color={COLOR_SYSTEM.get("Gray")[700]}
            style={{ whiteSpace: "pre-line" }}
          >
            {`• 가입 즉시 첫 결제가 이뤄지며, 등록 카드는 자동결제용으로 보관됩니다
• 다음 결제일은 마이페이지에서 확인할 수 있습니다
• 해지 시 현재 결제 주기 만료일까지 그대로 이용할 수 있습니다`}
          </Text>
        </InfoBox>

        <ConsentRow disabled={isProcessing}>
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={isProcessing}
            style={{ marginTop: 2 }}
          />
          <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[800]}>
            자동결제 등록 및{" "}
            <TermsLink onClick={handleOpenTerms}>정기결제 약관</TermsLink>에 동의합니다.
          </Text>
        </ConsentRow>
      </ContentSection>

      <FooterSection>
        <Flex
          alignItems="center"
          justifyContent="space-between"
          style={{ marginBottom: 16 }}
        >
          <Text textType="Body1Medium">결제 금액</Text>
          <Text textType="H3" color={COLOR_SYSTEM.get("Blue")[600]}>
            {formatKrw(selected.price)}
          </Text>
        </Flex>
        <Button
          size="Large"
          type="Text"
          variant="Contained"
          color="Primary"
          onClick={handleConfirm}
          disabled={!agreed || isProcessing}
          style={{ width: "100%" }}
        >
          {isProcessing ? (
            <Spinner size="Small" />
          ) : (
            `${formatKrw(selected.price)} 결제하고 구독 시작`
          )}
        </Button>
      </FooterSection>
    </SlideContainer>
  );
};

export default PremiumSubscribeSlide;
